/**
 * Purchase Order Module Documentation
 */

export const purchaseOrderDoc = {
  title: 'Purchase Order Module',
  content: `# Purchase Order Module Training Guide

## 1. Overview

The **Purchase Order (PO) Module** manages orders placed with your vendors/suppliers. This is the "buying" side of your business.

**Key Concept:** A Purchase Order is what you send to vendors. You are the buyer.

---

## 2. Expenses Module Overview

\`\`\`mermaid
flowchart LR
    subgraph Vendors["🏭 Vendor Management"]
        V[Vendors]
    end
    
    subgraph Orders["📋 Purchasing"]
        PO[Purchase Orders]
    end
    
    subgraph Bills["📄 Payables"]
        B[Bills to Pay]
    end
    
    subgraph Payments["💳 Payments"]
        P[Payments]
        CHK[Checks]
    end
    
    V --> PO
    PO --> B
    B --> P
    P --> CHK
    
    style V fill:#8B7355,color:#fff
    style PO fill:#3b82f6,color:#fff
    style B fill:#f59e0b,color:#fff
    style P fill:#22c55e,color:#fff
\`\`\`

---

## 3. Accessing the Module

**Navigation:** Admin Panel → Expenses → **Purchase Orders**

---

## 4. Related Modules

| Module | Purpose |
|--------|---------|
| **Vendors** | Manage vendor accounts |
| **Purchase Orders** | Orders to vendors |
| **Bills to Pay** | Vendor invoices |
| **Payments** | Payments to vendors |

---

## 5. Vendor Management

### Vendor Fields

| Field | Required | Description |
|-------|----------|-------------|
| **Vendor Name** | Yes | Company name |
| **Contact Name** | No | Primary contact |
| **Email** | No | Contact email |
| **Phone** | No | Contact phone |
| **Payment Terms** | No | Net 15, Net 30, etc. |

### Creating a Vendor

1. Navigate to **Expenses → Vendors**
2. Click **"+ Create New"** → **"Vendor"**
3. Enter **Vendor Name**
4. Add contact information
5. Set **Payment Terms**
6. Click **"Save"**

---

## 6. PO Field Reference

### Header Fields

| Field | Required | Description |
|-------|----------|-------------|
| **PO Number** | Auto | System-generated ID |
| **Vendor** | Yes | Select vendor |
| **Order Date** | Yes | Date PO created |
| **Expected Date** | No | Expected delivery |

### Line Items

| Field | Required | Description |
|-------|----------|-------------|
| **Description** | Yes | Item description |
| **Quantity** | Yes | Number of units |
| **Unit Cost** | Yes | Cost per unit |
| **Total** | Auto | Qty × Unit Cost |

---

## 7. Complete PO Flow

\`\`\`mermaid
flowchart TB
    subgraph Create["1️⃣ CREATE"]
        A[Select Vendor] --> B[Add Items]
        B --> C[Set Dates]
        C --> D[Save PO]
    end
    
    subgraph Send["2️⃣ SEND"]
        D --> E[Review PO]
        E --> F[Send to Vendor]
        F --> G[Status: Sent]
    end
    
    subgraph Receive["3️⃣ RECEIVE"]
        G --> H[Delivery Arrives]
        H --> I[Verify Items]
        I --> J[Status: Received]
    end
    
    subgraph Pay["4️⃣ PAY"]
        J --> K[Vendor Sends Bill]
        K --> L[Match to PO]
        L --> M[Record Payment]
    end
    
    style D fill:#fef08a
    style G fill:#93c5fd
    style J fill:#c4b5fd
    style M fill:#86efac
\`\`\`

---

## 8. Creating a Purchase Order

**Steps:**

1. Click **"+ Create New"** → **"Purchase Order"**
2. Select the **Vendor**
3. Set **Order Date** and **Expected Date**
4. Add **Line Items**
5. Add **Notes** for vendor
6. Click **"Save"**

---

## 9. Sending PO to Vendor

1. Open the Purchase Order
2. Review all details
3. Click **"Send"** or **"Email"**
4. Status changes to **"Sent"**

---

## 10. Receiving Goods

1. When delivery arrives, open the PO
2. Verify items match the PO
3. Note any discrepancies
4. Change status to **"Received"**

---

## 11. Matching PO to Bill

\`\`\`mermaid
flowchart LR
    PO[📋 Purchase Order] --> RCV[📦 Goods Received]
    RCV --> BILL[📄 Vendor Bill Arrives]
    BILL --> MATCH{Match PO?}
    MATCH -->|Yes| PAY[💳 Pay Bill]
    MATCH -->|No| DISPUTE[⚠️ Resolve Discrepancy]
    DISPUTE --> MATCH
    
    style PO fill:#3b82f6,color:#fff
    style PAY fill:#22c55e,color:#fff
    style DISPUTE fill:#ef4444,color:#fff
\`\`\`

When you receive a vendor invoice:

1. Navigate to **Bills to Pay**
2. Create new bill
3. **Link to PO**
4. Verify amounts match
5. Record payment when ready

---

## 12. PO Status Flow

\`\`\`mermaid
flowchart LR
    DRAFT[📝 Draft] --> SENT[📤 Sent] --> RECEIVED[📦 Received] --> CLOSED[✅ Closed]
    SENT --> CANCEL[❌ Cancelled]
    
    style DRAFT fill:#e5e7eb
    style SENT fill:#93c5fd
    style RECEIVED fill:#c4b5fd
    style CLOSED fill:#86efac
    style CANCEL fill:#fca5a5
\`\`\`

| Status | Description |
|--------|-------------|
| **Draft** | Not sent yet |
| **Sent** | Sent to vendor |
| **Received** | Goods received |
| **Closed** | Complete, paid |
| **Cancelled** | Cancelled |

---

## 13. Tips & Best Practices

### Do's ✅

- Always create PO BEFORE ordering
- Include customer order reference in notes
- Verify delivery against PO
- Match bills to POs

### Don'ts ❌

- Don't order without a PO
- Don't pay bills without matching
- Don't ignore discrepancies
- Don't delete POs - cancel them

---

*Last Updated: February 2026*
`
};
